import {createStore} from '@ngneat/elf';
import {
  withEntities,
  setEntities,
  withActiveId,
  setActiveId,
  selectActiveEntity,
  selectAllEntities,
  updateEntities
} from '@ngneat/elf-entities';
import {withRequestsCache, withRequestsStatus} from '@ngneat/elf-requests';
import {Injectable} from "@angular/core";

import {RestService} from 'src/app/shared/services/rest.service';
import {map, Observable, take} from 'rxjs';
import {AnimalShelterModel} from "../models/animal-shelter.model";

const storeName = 'animal-shelters';

export const store = createStore(
  {name: storeName},
  withEntities<AnimalShelterModel>(),
  withActiveId(),
  withRequestsCache<typeof storeName>(),
  withRequestsStatus<typeof storeName>()
);

@Injectable({providedIn: 'root'})
export class AnimalSheltersRepository {
  constructor(
    private readonly _restService: RestService
  ) {
    store.reset();
  }

  loadAnimalShelters() {
    this._restService.httpGet('/animal_shelters').pipe(take(1)).subscribe(shelters => {
      this.setAnimalShelters(shelters);
    });
  }

  setAnimalShelters(shelters: AnimalShelterModel[]) {
    store.update(setEntities(shelters));
  }

  setActiveAnimalShelter(entry: AnimalShelterModel) {
    if (!entry) {
      return;
    }
    store.update(setActiveId(entry.id));
  }

  getActiveAnimalShelter(): AnimalShelterModel | undefined {
    const state = store.state;
    const activeId = state.activeId;
    if (!activeId) {
      return undefined;
    }
    return state.entities[activeId] as AnimalShelterModel | undefined;
  }

  /**
   * Das aktive Tierheim als Strom: Beim Start wird es erst gesetzt, wenn der Benutzer geladen ist
   * (`AuthService`), und der Mandantenwechsel ändert es später erneut.
   */
  getActiveAnimalShelter$(): Observable<AnimalShelterModel | undefined> {
    return store.pipe(selectActiveEntity());
  }

  getAnimalShelters(): Observable<AnimalShelterModel[]> {
    return store.pipe(selectAllEntities());
  }

  get(id: number): Observable<AnimalShelterModel | undefined> {
    return store.pipe(selectAllEntities(), take(1), map(entities => entities.find(entity => entity.id === id)));
  }

  createShelter(shelter: Partial<AnimalShelterModel>): Observable<Partial<AnimalShelterModel>> {
    return this._restService.httpPost('/animal_shelters', shelter).pipe(
      map((createdShelter: AnimalShelterModel) => {
        store.update(setEntities([createdShelter]));
        return createdShelter;
      })
    );
  }

  updateShelter(id: number, shelter: Partial<AnimalShelterModel>): Observable<Partial<AnimalShelterModel>> {
    return this._restService.httpPut(`/animal_shelters/${id}`, shelter).pipe(
      map((updatedShelter: AnimalShelterModel) => {
        store.update(updateEntities(id, updatedShelter));
        return updatedShelter;
      })
    );
  }

  getShelterUsers(shelterId: number): Observable<number[]> {
    return this._restService.httpGet(`/animal_shelters/${shelterId}/users`).pipe(
      map((response: { boardMembers?: number[] }) => response?.boardMembers ?? [])
    );
  }

  updateShelterUsers(shelterId: number, boardMembers: number[]): Observable<any> {
    return this._restService.httpPut(`/animal_shelters/${shelterId}/users`, {boardMembers});
  }

  getAllShelters(): Observable<Partial<AnimalShelterModel>[]> {
    return store.pipe(selectAllEntities());
  }
}
